<script setup lang="ts">
import { Ref, inject, onMounted, ref } from 'vue'
import { type Supporter, TheFooter } from '@wastedondestiny/destiny-library'
import ActivityCard from '../components/ActivityCard.vue';
import { exoticActivities } from '../releaseDetail'
import { Activity, CollectionAccount, LeaderboardPage } from '../types'
import { ActivityId, ActivityType } from '../enums/activities'
import Leaderboard from '../components/Leaderboard.vue'
import { GroupV2 } from 'quria'
import { Expansions } from '../enums/expansions'

const now = Date.now()
const nextReset = new Date(now + 86400000 * 3)

const supporters = inject<Ref<Supporter[]>>('supporters')

const activity = exoticActivities.find(x => x.id === ActivityId.CrotasEnd)!
const clan = {
  name: 'My clan',
  memberCount: 50,
  groupId: '123'
} as GroupV2
const members: CollectionAccount[] = [
  {
    displayName: '4204nx10usF4bul0us#4563',
    membershipId: '123',
    membershipType: 2,
    characters: [{ characterId: '1' }, { characterId: '2' }, { characterId: '3' }],
    dateLastPlayed: new Date(now),
    fireteam: [],
    versionsOwned: Expansions.Forsaken | Expansions.Shadowkeep | Expansions.TheWitchQueen,
    items: { [exoticActivities[0].collectible]: true, [exoticActivities[6].collectible]: true },
    private: false
  },
  {
    displayName: 'Fl00rOny3k4chukw69#4523',
    membershipId: '234',
    membershipType: 5,
    characters: [{ characterId: '1' }, { characterId: '2' }],
    dateLastPlayed: new Date(now),
    fireteam: [],
    versionsOwned: Expansions.Lightfall | Expansions.TheWitchQueen,
    items: { [exoticActivities[6].collectible]: true, [exoticActivities[9].collectible]: true, [exoticActivities[11].collectible]: true },
    private: false
  },
  {
    displayName: 'RubidiumToad#0341',
    membershipId: '345',
    membershipType: 1,
    characters: [{ characterId: '1' }, { characterId: '2' }],
    dateLastPlayed: new Date(now),
    fireteam: [],
    versionsOwned: Expansions.Lightfall | Expansions.BeyondLight | Expansions.Shadowkeep | Expansions.TheWitchQueen,
    items: { [exoticActivities[2].collectible]: true },
    private: false
  },
  {
    displayName: 'C4ul1fl0w3rM4nd4r1n#8344',
    membershipId: '456',
    membershipType: 3,
    characters: [{ characterId: '1' }],
    dateLastPlayed: new Date(now),
    fireteam: [],
    versionsOwned: Expansions.Lightfall | Expansions.BeyondLight | Expansions.Shadowkeep | Expansions.TheWitchQueen | Expansions.Forsaken,
    items: { '123': true },
    private: false
  },
  {
    displayName: 'Ghost the Famous#1422',
    membershipId: '567',
    membershipType: 1,
    characters: [{ characterId: '1' }, { characterId: '2' }, { characterId: '3' }],
    dateLastPlayed: new Date(now),
    fireteam: [],
    versionsOwned: Expansions.BeyondLight | Expansions.Shadowkeep | Expansions.Forsaken,
    items: { [exoticActivities[4].collectible]: true, [exoticActivities[6].collectible]: true, [activity.collectible]: true },
    private: false
  }
]
const activities: Record<string, Activity[]> = {
  '123': [
    { activityId: ActivityId.CrotasEnd, characterId: '1', completed: true, id: '1', modes: [] },
    { activityId: ActivityId.CrotasEnd, characterId: '2', completed: true, id: '2', modes: [] },
    { activityId: ActivityId.WarlordsRuin, characterId: '1', completed: true, id: '3', modes: [] }
  ],
  '345': [
    { activityId: ActivityId.CrotasEnd, characterId: '2', completed: true, id: '2', modes: [] },
    { activityId: ActivityId.WarlordsRuin, characterId: '1', completed: true, id: '3', modes: [] }
  ],
  '456': [
    { activityId: ActivityId.CrotasEnd, characterId: '1', completed: true, id: '1', modes: [] }
  ]
}
const rotations: Record<string, string> = {
  'Deep Stone Crypt': 'Active Weekly Raid Challenge'
}
const page: LeaderboardPage = {
  players: members,
  count: 5,
  page: 1,
  total: 50
}

const showDemo = ref(false)

onMounted(() => {
  setTimeout(() => showDemo.value = true, 0)
})
</script>

<template>
  <div class="w-full max-w-[48rem] px-2 py-4 text-zinc-200 mx-auto pb-20">
    <h1 id="about" class="text-center text-4xl my-2 border-zinc-500 border-b pb-2">About loot.report</h1>
    <div class="grid about-grid gap-8">
      <div class="about-grid-a text-zinc-200">
        <p class="indent-4 text-justify">
          loot.report is a website designed to help you track your clan's activity completion for Destiny 2, allowing you to plan raid and dungeon runs
          with clanmates to help them acquire new exotics. Often, players may not remember what activities they completed or if they acquired certain exotics,
          and that's where loot.report comes in. With loot.report, you can just enter your clan name to access a list of all your clanmates and their history.
        </p>
        <h2 class="text-2xl my-2 font-semibold">How to use</h2>
        <p class="indent-4 text-justify">
          Type your or a friend's Destiny 2 clan name into the search bar above to add its members to the list. From there, you can easily compare exotics
          owned and attempts at getting them by clan members. If you want to replace the list with another clan, just search a new clan in the search bar.
        </p>
      </div>
      <div class="about-grid-b" v-if="showDemo">
        <h2 class="text-2xl my-2 font-semibold">Demonstration</h2>
        <ActivityCard
          class="mx-auto text-black"
          :activity="activity"
          :activities="activities"
          :is-open="{ missing: true, acquired: false, unavailable: false }"
          :members="members"
          :reset="nextReset"
          :rotations="rotations"
          :loading="['123', '234', '345', '456', '567']"
          is-demo
        />
      </div>
      <div class="about-grid-c">
        <Leaderboard
          class="h-[31.5rem] sm:h-[20.5rem]"
          :activities="exoticActivities"
          :clan="clan"
          :filter="ActivityType.Raid"
          :page="page"
          is-demo
        />
      </div>
    </div>
    <div class="text-zinc-200 text-justify">
      <h2 class="text-2xl my-2 font-semibold">Disclaimer</h2>
      <p class="indent-4">
        loot.report is not affiliated with <a href="https://www.bungie.net" target="_blank" class="opacity-75 hover:opacity-100">Bungie</a>. Destiny
        is a registered trademark of Bungie.
      </p>
      <h2 class="text-2xl my-2 font-semibold">Supporters</h2>
      <p class="indent-4">
        loot.report could not survive without the support of Guardians like you. If you like seeing tools like loot.report,
        <a href="https://wastedondestiny.com" target="_blank" class="opacity-75 hover:opacity-100">Time Wasted on Destiny</a>,
        <a href="https://crimson.report" target="_blank" class="opacity-75 hover:opacity-100">crimson.report</a> or
        <a href="https://guesstiny.com" target="_blank" class="opacity-75 hover:opacity-100">Guesstiny</a> exist, please
        consider <a href="http://www.patreon.com/wastedondestiny" target="_blank" class="opacity-75 hover:opacity-100">supporting me on Patreon</a>.
        Here are the top supporters:
      </p>
      <ul class="text-left list-disc ml-6">
        <li v-for="supporter in supporters">
          <span>{{ supporter.name }} ({{ supporter.tier }})</span>
        </li>
      </ul>
      <h2 class="text-2xl my-2 font-semibold">Credits</h2>
      <p class="indent-4">
        loot.report is made possible thanks to:
      </p>
      <ul class="text-left list-disc ml-6 mb-10">
        <li><a href="https://www.bungie.com" target="_blank" class="opacity-75 hover:opacity-100">Bungie</a>, who is giving us the best game API out there</li>
        <li>All my friends and supporters for helping me crush bugs and test the website</li>
      </ul>
    </div>
    <TheFooter about name="loot.report" use-playwire>
      <a href="https://karmageddon.games" target="_blank">&copy; Karmageddon Games {{ (new Date()).getFullYear() }}</a>
      <template #privacy>
        <RouterLink class="!decoration-1 !decoration-dotted" to="/privacy">Privacy</RouterLink>
      </template>
      <template #ad>
        <!-- <ins class="adsbygoogle w-[320px] h-[100px] sm:w-[468px] sm:h-[60px] lg:w-[728px] lg:h-[90px] block z-10 mx-auto"
          data-ad-client="ca-pub-6332373031553935"
          data-ad-slot="8162200044"
          data-ad-format="auto"
          data-full-width-responsive="true"></ins> -->
        <div id="footer-bottom" class="w-full h-[280px] mx-auto flex justify-center"></div>
      </template>
    </TheFooter>
  </div>
</template>

<style scoped lang="postcss">
.about-grid {
  grid-template-areas: 'a a'
                       'b b'
                       'c c';

  @media screen(md) {
    grid-template-areas: 'a b'
                         'a b'
                         'c c';
  }
}

.about-grid-a {
  grid-area: a;
}

.about-grid-b {
  grid-area: b;
}

.about-grid-c {
  grid-area: c;
}
</style>
